import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { MapPin, Eraser, Camera, Paperclip, Check } from "lucide-react";
import { toast } from "sonner";

export const fieldKey = (f, i) => f.id || String(i);

export function SignaturePad({ value, onChange }) {
  const ref = useRef();
  const drawing = useRef(false);
  const { t } = useI18n();
  useEffect(() => {
    const c = ref.current; const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0F172A";
    if (!value) ctx.clearRect(0, 0, c.width, c.height);
  }, [value]);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return [(p.clientX - r.left) * (ref.current.width / r.width), (p.clientY - r.top) * (ref.current.height / r.height)]; };
  const start = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); const [x, y] = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const [x, y] = pos(e); ctx.lineTo(x, y); ctx.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, ref.current.width, ref.current.height); onChange(""); };
  return (
    <div>
      <canvas ref={ref} width={600} height={220} data-testid="signature-pad"
        className="w-full h-36 bg-white border-2 border-dashed border-[var(--border)] rounded-lg touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-[var(--text-3)]">{value ? <span className="text-[var(--green)] flex items-center gap-1"><Check size={12} /> {t("signature")}</span> : t("sign_here")}</span>
        <button type="button" onClick={clear} className="text-xs flex items-center gap-1 text-[var(--red)] p-2 -m-2" data-testid="signature-clear"><Eraser size={13} /> {t("clear")}</button>
      </div>
    </div>
  );
}

export function GpsField({ value, onChange }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const locate = () => {
    if (!navigator.geolocation) return toast.error(t("gps_unavailable"));
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { onChange({ lat: +p.coords.latitude.toFixed(6), lng: +p.coords.longitude.toFixed(6), accuracy: Math.round(p.coords.accuracy), at: new Date().toISOString() }); setBusy(false); },
      () => { toast.error(t("gps_unavailable")); setBusy(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" onClick={locate} disabled={busy} className="h-11 gap-2" data-testid="gps-btn"><MapPin size={16} /> {value ? t("gps_update") : t("gps_get")}</Button>
      {value && <span className="text-xs font-mono-num text-[var(--text-3)]" data-testid="gps-value">{value.lat}, {value.lng} (±{value.accuracy}m)</span>}
    </div>
  );
}

export function FormFields({ fields, values, onChange, uploadFile }) {
  const { t, tn } = useI18n();
  const set = (k, v) => onChange({ ...values, [k]: v });
  const label = (f) => (typeof f.label === "string" ? f.label : tn(f.label)) || f.type;
  return (
    <div className="space-y-3" data-testid="custom-form-fields">
      {fields.map((f, i) => {
        const k = fieldKey(f, i); const v = values[k];
        const cls = "h-12 text-base";
        return (
          <div key={k} data-testid={`cf-${f.type}-${k}`}>
            <label className="text-xs uppercase font-semibold text-[var(--text-3)] flex gap-1">{label(f)}{f.required && <span className="text-[var(--red)]">*</span>}</label>
            {f.type === "text" && <Input className={cls} value={v || ""} onChange={(e) => set(k, e.target.value)} />}
            {f.type === "number" && <Input className={cls} type="number" inputMode="decimal" value={v ?? ""} onChange={(e) => set(k, e.target.value === "" ? "" : +e.target.value)} />}
            {f.type === "date" && <Input className={cls} type="date" value={v || ""} onChange={(e) => set(k, e.target.value)} />}
            {f.type === "time" && <Input className={cls} type="time" value={v || ""} onChange={(e) => set(k, e.target.value)} />}
            {f.type === "comment" && <Textarea className="text-base" value={v || ""} onChange={(e) => set(k, e.target.value)} />}
            {f.type === "checkbox" && <label className="flex items-center gap-2 h-12"><Checkbox checked={!!v} onCheckedChange={(c) => set(k, !!c)} /> {t("yes")}</label>}
            {f.type === "select" && (
              <select className="border rounded-md h-12 px-2 text-base w-full bg-[var(--card)]" value={v || ""} onChange={(e) => set(k, e.target.value)}>
                <option value="">—</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            {(f.type === "photo" || f.type === "file") && (
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 h-12 tactical-card text-sm font-semibold">
                {f.type === "photo" ? <Camera size={18} /> : <Paperclip size={18} />} {v ? (v.name || t("uploaded")) : t("upload")}
                <input type="file" accept={f.type === "photo" ? "image/*" : undefined} capture={f.type === "photo" ? "environment" : undefined} className="hidden"
                  onChange={async (e) => { const file = e.target.files[0]; if (!file) return; const r = await uploadFile(file); if (r) set(k, r); }} />
              </label>
            )}
            {f.type === "signature" && <SignaturePad value={v} onChange={(d) => set(k, d)} />}
            {f.type === "gps" && <GpsField value={v} onChange={(g) => set(k, g)} />}
          </div>
        );
      })}
    </div>
  );
}

export async function uploadDataUrl(dataUrl, name = "signature.png") {
  const blob = await (await fetch(dataUrl)).blob();
  const fd = new FormData(); fd.append("file", blob, name); fd.append("entity_type", "report"); fd.append("phase", "signature");
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.id;
}
